/**
 * Wire types for the author's content history view.
 *
 * Shared so the server's change map and the client's rendering cannot drift —
 * the diff shapes themselves live in `shared/diff`.
 */

import type { BlockDiffRow, SegmentChange, StructuralChange } from './diff/types'

export interface HistoryCommit {
  sha: string
  shortSha: string
  /** ISO 8601. */
  date: string
  subject: string
}

/** Cheap totals shown on a timeline row. */
export interface HistorySummary {
  sectionsChanged: number
  wordsAdded: number
  wordsRemoved: number
  sectionsAdded: number
  sectionsRemoved: number
  /** Source files the commit touched, including ones whose prose did not change. */
  filesTouched: number
  /** True when nothing but structure changed — a move, a publish, a reorder. */
  structuralOnly: boolean
}

export interface HistoryTimelineEntry extends HistoryCommit {
  summary: HistorySummary | null
}

export interface HistoryTimelineResponse {
  available: boolean
  reason?: string
  entries: HistoryTimelineEntry[]
  hasMore: boolean
  /**
   * Where to resume from.
   *
   * Commits that changed nothing are dropped from the timeline, so the number of
   * rows returned no longer matches the number of commits consumed — the client
   * has to page by this rather than by how many entries it has.
   */
  nextSkip: number
}

/** One source file's worth of changes in the change map. */
export interface FileChangeGroup {
  filename: string
  displayTitle: string
  kind: 'added' | 'removed' | 'edited'
  /** File-level changes that leave the prose untouched (publish, rename). */
  structural: StructuralChange[]
  entries: SegmentChange[]
}

/** A chapter-level change, which lives in `_order.json` and touches no prose. */
export interface ChapterChange {
  kind: 'added' | 'removed' | 'renamed' | 'sections-changed'
  id: string
  title: string
  titleBefore?: string
  sectionsAdded: number
  sectionsRemoved: number
}

export interface ChangeMapResponse {
  available: boolean
  reason?: string
  from: HistoryCommit | null
  to: HistoryCommit | null
  files: FileChangeGroup[]
  /**
   * Chapter-level changes. Not rendered: in this book's history a chapter never
   * moves without its sections moving too, so showing them repeats the section
   * list. Kept because the generated commit names do mention them, where
   * "chapter X created" is worth the words.
   */
  chapters: ChapterChange[]
  totals: HistorySummary
}

export interface SectionDiffResponse {
  available: boolean
  reason?: string
  key: string
  filename: string
  headingBlockId: string | null
  displayTitle: string
  ancestorLabels: string[]
  /** True when the heading carries `^` — a reader-visible ChapterSection. */
  isChapterSection: boolean
  kind: SegmentChange['kind']
  rows: BlockDiffRow[]
  wordsAdded: number
  wordsRemoved: number
  structural: StructuralChange[]
}

/**
 * Every changed section's diff for one range, in change-map order.
 *
 * The whole range arrives at once rather than a section at a time: the diff
 * pane reads as one continuous document, which is how you review a day's work.
 * A large commit runs ~220 KB, which is cheap enough not to bother paginating.
 */
export interface AllDiffsResponse {
  available: boolean
  reason?: string
  sections: SectionDiffResponse[]
  /** True when the range held more sections than the cap allowed. */
  truncated: boolean
  totalSections: number
}

/**
 * Stands in for a commit sha to mean "the manuscript as it is right now".
 *
 * Not a git revision: it resolves to whatever the content service currently
 * serves — Mongo normally, the working files during a content session — which
 * is exactly what the editor is showing. Comparing a commit against this
 * answers "how far has the book moved since then", which no pair of commits
 * can.
 */
export const CURRENT_REVISION = 'current'

/**
 * The fixed subjects the session scripts used before they started naming
 * commits after what actually changed.
 *
 * Both describe the *mechanism* that made the commit rather than the work in
 * it, which is no help in a timeline — nine hundred rows repeating two strings.
 * A commit carrying one of these gets its generated summary as a headline
 * instead.
 */
const PLACEHOLDER_SUBJECTS = new Set(['Sync from Mongo (pre-session snapshot)', 'Content session update'])

/** True when a commit's subject says nothing about what changed in it. */
export function isPlaceholderCommitSubject(subject: string): boolean {
  return PLACEHOLDER_SUBJECTS.has(subject.trim())
}
